import React, { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { i18n } from "@locales";

type IContext = Partial<Record<string, Record<string, string>>>;

const I18nContext = createContext<IContext | undefined>(undefined);

/**
 * I18nProvider enables dynamic translation overrides at runtime.
 *
 * Purpose:
 * - Allows customization of translations per event/organization
 * - Provides a context for components to access custom translations
 * - Falls back to default translations when no override exists
 *
 * The provider accepts i18nOverrides which contain custom translations
 * keyed by translation key and locale.
 *
 * Components use the useLocalize hook to access these overrides,
 * which checks the context first before falling back to default i18n
 * translations.
 */
export default function I18nProvider({
  i18nOverrides,
  children,
}: {
  i18nOverrides: IContext;
  children: ReactNode;
}) {
  const value = useMemo(() => i18nOverrides, [i18nOverrides]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to get localized strings with support for runtime overrides.
 *
 * @param key - Translation key to look up
 * @returns The custom translation if available in context, otherwise the default translation
 *
 * Priority order:
 * 1. Custom translation from I18nContext (if exists and non-empty)
 * 2. Default translation from i18n.t()
 *
 * This enables per-event or per-organization customization of UI text
 * without modifying the base translation files.
 */
export function useLocalize(key: string) {
  const context = useContext(I18nContext);

  const locale = i18n.getLocale;

  // Get the default translation as fallback
  const fallback = i18n.t(key);

  if (!key) return fallback;

  // If context is not available (shouldn't happen with RootComponent), return fallback
  if (!context) return fallback;

  const entry = context[key];
  if (!entry) return fallback;

  // Pick only the specified locale, no variants
  const found = entry[locale];

  // Return the found value if it's a non-empty string, otherwise fallback
  if (typeof found === "string" && found.trim() !== "") {
    return found;
  }

  return fallback;
}
